import { and as kqlAnd, elementIdOf, isElementIdSegment, serialize } from './kql';
import {
	astToRows,
	elementGroupIds,
	elementGroupSegment,
	findField,
	isElementGroupSegment,
	isEntriesCmp,
	missingMatches,
	rowNodes,
	rowToNode,
	rowsToText,
	isSpanRow,
} from './kqlBuilder';
import { collapseSpanRows } from './datePresets';
import {
	MATCH_ALL,
	MATCH_ANY,
	isGroupItem,
	readGrouped,
	writeGrouped,
} from './conditionGroups';

/**
 * THE FILTER BUILDER'S GROUPS (KPR-978, KPE-010): one level of "any of these"
 * over FilterBuilder's rows, through the shape ConditionBuilder already uses
 * (utils/conditionGroups.js, KPR-951), so the two builders group the same way
 * and a query written in one opens in the other.
 *
 * FilterBuilder's rows are richer than ConditionBuilder's. Three of them are
 * more than one comparison in the text:
 *
 *   a date SPAN          "This week": `created >= … and created < …`
 *   an ELEMENT GROUP     "Status · any of these cards": `#a = open or #b = open`
 *                        (`#a != x and #b != x` where a missing value matches)
 *   a repeating group's  `ingredient has (…)`, `not ingredient has (…)`
 *   `has` row
 *
 * So a row is read with `astToRows` — the builder's own reader, which knows all
 * three — and a node is ONE row when that reading collapses to exactly one.
 *
 * WHAT THE GRAMMAR FLATTENS. `a and (b and c)` is `a and b and c`, and
 * `a or (b or c)` is `a or b or c`. A span (an `and`) written into an "all"
 * line, or an element group's `or` written into an "any" line, loses its
 * parentheses and becomes siblings. Reading puts them back: each run of rows
 * between groups is read again the way the builder reads a plain `and` (spans
 * and the negative element groups rejoin), and in an "any" or "none" line,
 * neighbouring comparisons on elements by id that differ only in the element
 * are one "any of these cards" row again. Nothing changes meaning: it is the
 * drawing that comes back.
 *
 * WHAT IS LEFT ALONE. A query every row of which `astToRows` already reads —
 * everything the builder showed before groups — opens exactly as it did, and
 * one with no group and no "any" writes exactly as it did (`rowsToText`).
 */

/** A row's ONE node for the group writer: a span as its two bounds joined, every other row as `rowToNode` writes it. */
export function filterRowNode(row) {
	if (isSpanRow(row)) {
		const nodes = rowNodes(row);
		return nodes.length ? (nodes.length === 1 ? nodes[0] : kqlAnd(nodes)) : null;
	}
	return rowToNode(row);
}

/** One node as the ONE row that shows it, or null when no row (or more than one) does. */
export function filterRowOfNode(node) {
	const rows = astToRows(node);
	if (!rows) return null;
	const collapsed = collapseSpanRows(rows);
	return collapsed.length === 1 ? collapsed[0] : null;
}

/** Rows of an "all" line read again as one `and`: spans and negative element groups rejoin. */
function rereadAll(rows) {
	if (rows.length < 2) return rows;
	const args = rows.flatMap(rowNodes);
	const read = astToRows(args.length === 1 ? args[0] : { op: 'and', args });
	return read ? collapseSpanRows(read) : rows;
}

/** The element ids a row names on its own path when it can join an "any of these cards" row, else null. */
function idBranchIds(row) {
	if (!row || isSpanRow(row) || row.facet || row.forceElement || isEntriesCmp(row.cmp) || row.entryRows) return null;
	if (!Array.isArray(row.path) || row.path.length !== 1 || missingMatches(row.cmp)) return null;
	const [segment] = row.path;
	if (isElementGroupSegment(segment)) return elementGroupIds(segment);
	return isElementIdSegment(segment) ? [elementIdOf(segment)] : null;
}

const comparisonKey = row => JSON.stringify([row.cmp, row.value ?? null]);

/**
 * Rows of an "any" (or "none") line with neighbouring id comparisons alike in
 * all but the element joined into one group row. With a vocabulary
 * (`fields`), only elements it names are joined: a row's label names the
 * elements it knows, so an id this page cannot see (who-sees-what text is read
 * in every sub-collection too) stays a row of its own, said as such, rather
 * than vanishing from a joined row's words (the KPR-978 review).
 */
function rereadAny(rows, fields) {
	const known = (ids) => {
		if (!ids || !fields) return ids;
		return ids.every(id => !findField(fields, [`#${id}`]).unknown) ? ids : null;
	};
	const out = [];
	rows.forEach((row) => {
		const ids = known(idBranchIds(row));
		const last = out[out.length - 1];
		const lastIds = last ? known(idBranchIds(last)) : null;
		if (ids && lastIds && comparisonKey(row) === comparisonKey(last) && !ids.some(id => lastIds.includes(id))) {
			out[out.length - 1] = {
				...last,
				path: [elementGroupSegment([...lastIds, ...ids])],
				forceElement: false,
				facet: false,
			};
			return;
		}
		out.push(row);
	});
	return out;
}

/** Rows read again in the line they sit in: `match` is that line's join. */
function rereadLine(rows, match, fields) {
	return match === MATCH_ALL ? rereadAll(rows) : rereadAny(rows, fields);
}

/** The top line's items with each run of rows between groups read again in its line. */
function rereadItems(items, match, fields) {
	const out = [];
	let run = [];
	const flush = () => {
		if (run.length) out.push(...rereadLine(run, match, fields));
		run = [];
	};
	items.forEach((item) => {
		if (isGroupItem(item)) {
			flush();
			out.push({ ...item, rows: rereadLine(item.rows || [], item.match, fields) });
		} else {
			run.push(item);
		}
	});
	flush();
	return out;
}

/**
 * A parsed query as the builder's top line: `{ match, items }`, where an item
 * is a row or a group `{ id, group: true, match, rows }` — or null when the
 * query is deeper than one level of groups (or holds a condition no row
 * shows), which the builder shows read-only and never rewrites. No query is
 * no items. `fields`, the page's vocabulary, is optional: given, an "any of
 * these cards" row is joined only from elements it names (rereadAny).
 */
export function readFilterGroups(ast, { fields = null } = {}) {
	if (!ast) return { match: MATCH_ALL, items: [] };
	// Everything the builder read before groups opens exactly as it did.
	const flat = astToRows(ast);
	if (flat) return { match: MATCH_ALL, items: collapseSpanRows(flat) };
	const tree = readGrouped(ast, filterRowOfNode);
	if (!tree) return null;
	return { match: tree.match, items: rereadItems(tree.items, tree.match, fields) };
}

/** Does this top line need more than a plain `and` of rows? */
export const isGroupedLine = (items, match) => match === MATCH_ANY || (items || []).some(isGroupItem);

/**
 * The top line as canonical text ('' while nothing is complete). A line with
 * no group joined by "all" is written exactly as the builder always wrote it.
 */
export function writeFilterGroups(items, match = MATCH_ALL) {
	if (!isGroupedLine(items, match)) return rowsToText(items);
	const node = writeGrouped(items, match, filterRowNode);
	return node ? serialize(node) : '';
}

/**
 * The top line in words: each row as `describeRow(row)` says it, joined by
 * "and" / "or", a group in parentheses and a "none" group as `not (…)` — the
 * convention every other plain sentence of a filter uses (utils/drivenSummary).
 * '' while nothing is complete.
 */
export function describeFilterGroups(tree, describeRow) {
	if (!tree) return '';
	const join = match => (match === MATCH_ANY ? ' or ' : ' and ');
	const rowWords = rows => (rows || []).map(row => describeRow(row)).filter(Boolean);
	const parts = (tree.items || []).map((item) => {
		if (!isGroupItem(item)) return describeRow(item);
		const words = rowWords(item.rows);
		if (!words.length) return '';
		if (item.match === 'none') return `not (${words.join(' or ')})`;
		return words.length === 1 ? words[0] : `(${words.join(join(item.match))})`;
	}).filter(Boolean);
	return parts.join(join(tree.match));
}
